import { useEffect } from "react";
import JsonLd from "@/components/site/JsonLd";
const SITE_URL =
  process.env.REACT_APP_SITE_URL ||
  (typeof window !== "undefined" ? window.location.origin : "https://gfysdesigns.com");
const TITLE =
  "Garden Fountains Yard Sculptures | Handcrafted in Villa Rica, GA";
const DESCRIPTION =
  "Handcrafted concrete garden fountains and yard sculptures, made in Villa Rica, GA. Repairs, cleanings, and deliveries available within the United States for a small fee.";
const HERO_IMG = `${SITE_URL}/images/og-image-v3.jpg`;

export const AREA_SERVED = [
  { "@type": "Country", name: "United States" },
  { "@type": "State", name: "Georgia" },
  { "@type": "Place", name: "West Georgia" },
  { "@type": "City", name: "Villa Rica, GA" },
  { "@type": "City", name: "Carrollton, GA" },
  { "@type": "City", name: "Douglasville, GA" },
  { "@type": "City", name: "Newnan, GA" },
  { "@type": "City", name: "Atlanta, GA" },
  { "@type": "City", name: "Marietta, GA" },
  { "@type": "City", name: "Dallas, GA" },
  { "@type": "City", name: "Hiram, GA" },
  { "@type": "City", name: "Powder Springs, GA" },
  { "@type": "City", name: "Bremen, GA" },
  { "@type": "City", name: "Temple, GA" },
];
export const CONTACT_POINT = {
  "@type": "ContactPoint",
  telephone: "+1-678-769-9652",
  contactType: "customer service",
  areaServed: "US",
  availableLanguage: ["en"],
};

const LOCAL_BUSINESS = {
  "@context": "https://schema.org",
  "@type": ["LocalBusiness", "Store", "HomeAndConstructionBusiness"],
  "@id": `${SITE_URL}/#business`,
  name: "Garden Fountains Yard Sculptures",
  alternateName: [
    "Garden Fountains Yard Sculptures",
    "Chris's Garden Fountains Villa Rica",
  ],
  description: DESCRIPTION,
  url: SITE_URL,
  telephone: "+1-678-769-9652",
  image: [HERO_IMG, `${SITE_URL}/images/fountain-mansion-marble.jpg`, `${SITE_URL}/images/yard-banner-wide.jpg`],
  logo: HERO_IMG,
  priceRange: "$$",
  paymentAccepted: "Cash, Check",
  currenciesAccepted: "USD",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Villa Rica",
    addressRegion: "GA",
    postalCode: "30180",
    addressCountry: "US",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 33.7301,
    longitude: -84.9219,
  },
  areaServed: AREA_SERVED,
  founder: { "@type": "Person", name: "Chris" },
  knowsAbout: [
    "Garden Fountains",
    "Wall Fountains",
    "Yard Sculptures",
    "Garden Statues",
    "Cast Stone Statues",
    "Custom Outdoor Pieces",
    "Stone Urns and Planters",
    "Water Features",
    "Memorial Statues",
    "Angel Statues",
    "Cherub Statues",
    "Concrete Garden Art",
  ],
  makesOffer: [
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Custom Concrete Fountain Design and Installation" } },
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Fountain Repair, Cleaning, Maintenance and Winterizing" } },
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Installation of Customer-Purchased Fountains" } },
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Delivery and Setup of Garden Pieces" } },
  ],
  sameAs: ["https://www.facebook.com/gardenfountains.yardsculptures"],
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      opens: "09:00",
      closes: "18:00",
    },
  ],
  contactPoint: CONTACT_POINT,
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "Where are Garden Fountains Yard Sculptures located?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "We are based in Villa Rica, Georgia, about 35 miles west of Atlanta. Repairs, cleanings, and deliveries are available within the United States for a small fee.",
      },
    },
    {
      "@type": "Question",
      name: "What do pieces cost?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Small statues start around $10-15, bird baths and fountains are priced per piece — see the Available Pieces page for current prices, or call (678) 769-9652 for an exact quote.",
      },
    },
    {
      "@type": "Question",
      name: "Do you deliver nationwide?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. Delivery is available throughout the United States for a small fee. Contact Chris at (678) 769-9652 with your location and the piece you're interested in for a delivery quote.",
      },
    },
    {
      "@type": "Question",
      name: "Can I order a custom garden fountain or yard sculpture?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes — Chris works with each customer on size, finish, style, and placement. Call or text (678) 769-9652 to start a custom piece for your garden, yard, or property.",
      },
    },
    {
      "@type": "Question",
      name: "How do I know if a piece on the website is still available?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Pieces come and go faster than the site can keep up. Pieces may be available to view in Villa Rica or at Red Door Antiques in LaGrange. Please call or text Chris at (678) 769-9652 to confirm a piece is still available, and where to see it, before you make the drive.",
      },
    },
    {
      "@type": "Question",
      name: "I bought a fountain somewhere else. Can Chris install it?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. Chris installs fountains purchased elsewhere. Call Chris at (678) 769-9652 to discuss your project, or text photos of the fountain and the space where you'd like it installed. Scope, location, and the quote are confirmed directly with Chris.",
      },
    },
  ],
};

const VIDEO_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Garden Fountains Yard Sculptures Videos",
  itemListElement: [
    {
      "@type": "VideoObject",
      position: 1,
      name: "7-Minute Garden Walkthrough — Fountains and Sculptures in Villa Rica, GA",
      description:
        "A full walkthrough of Chris's garden and yard in Villa Rica, Georgia, showing handcrafted fountains, statues, and custom pieces among the flowers.",
      thumbnailUrl: `${SITE_URL}/images/walkthrough-poster.jpg`,
      contentUrl: `${SITE_URL}/videos/garden-walkthrough.mp4`,
      uploadDate: "2026-06-01",
      duration: "PT7M",
    },
    {
      "@type": "VideoObject",
      position: 2,
      name: "Featured Backyard Fountain Installation — Running Water",
      description:
        "A handcrafted garden fountain delivered, set up, and running in a customer's backyard in Georgia.",
      thumbnailUrl: `${SITE_URL}/images/featured-poster-1.jpg`,
      contentUrl: `${SITE_URL}/videos/featured-1.mp4`,
      uploadDate: "2026-05-01",
    },
    {
      "@type": "VideoObject",
      position: 3,
      name: "The Clover Fountain Running — Handcrafted French Village Style Fountain",
      description:
        "The Clover, a handcrafted French village style garden fountain by Garden Fountains Yard Sculptures, running with water.",
      thumbnailUrl: `${SITE_URL}/images/clover-run-1-poster.jpg`,
      contentUrl: `${SITE_URL}/videos/clover-run-1.mp4`,
      uploadDate: "2026-05-01",
    },
    {
      "@type": "VideoObject",
      position: 4,
      name: "The Majestic Fountain Running — Large Tiered Garden Fountain",
      description:
        "The Majestic, a large handcrafted tiered garden fountain with mix-and-match toppers, running with water in Villa Rica, GA.",
      thumbnailUrl: `${SITE_URL}/images/majestic-run-poster.jpg`,
      contentUrl: `${SITE_URL}/videos/majestic-run.mp4`,
      uploadDate: "2026-05-01",
    },
    {
      "@type": "VideoObject",
      position: 5,
      name: "Backyard Pond & Waterfall Restoration — Running Again",
      description:
        "A stone pond and waterfall in Georgia repaired, relined, and brought back to life by Garden Fountains Yard Sculptures.",
      thumbnailUrl: `${SITE_URL}/images/pond-restoration-poster.jpg`,
      contentUrl: `${SITE_URL}/videos/pond-restoration.mp4`,
      uploadDate: "2026-07-16",
    },
    {
      "@type": "VideoObject",
      position: 6,
      name: "Mermaid Shell Fountain Running — Handcrafted in Villa Rica, GA",
      description:
        "A handcrafted mermaid shell fountain with a soft blue bowl, delivered, set, and running at its new Georgia home.",
      thumbnailUrl: `${SITE_URL}/images/mermaid-fountain-run-poster.jpg`,
      contentUrl: `${SITE_URL}/videos/mermaid-fountain-run.mp4`,
      uploadDate: "2026-07-22",
    },
  ],
};

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [{ "@type": "ListItem", position: 1, name: "Home", item: SITE_URL }],
};

const META = [
  { selector: 'meta[name="description"]', attrs: { name: "description", content: DESCRIPTION } },
  {
    selector: 'meta[name="keywords"]',
    attrs: {
      name: "keywords",
      content:
        "garden fountains Georgia, yard sculptures Georgia, garden statues Villa Rica, handcrafted fountains Atlanta, custom garden pieces southeast, cast stone statues GA, water features Carrollton, garden urns Newnan, outdoor sculptures Douglasville, memorial statues Georgia, angel statues, cherub statues, wall fountains, concrete garden art",
    },
  },
  { selector: 'meta[property="og:title"]', attrs: { property: "og:title", content: TITLE } },
  { selector: 'meta[property="og:description"]', attrs: { property: "og:description", content: DESCRIPTION } },
  { selector: 'meta[property="og:type"]', attrs: { property: "og:type", content: "website" } },
  { selector: 'meta[property="og:url"]', attrs: { property: "og:url", content: `${SITE_URL}/` } },
  { selector: 'meta[property="og:image"]', attrs: { property: "og:image", content: HERO_IMG } },
  { selector: 'meta[property="og:site_name"]', attrs: { property: "og:site_name", content: "Garden Fountains Yard Sculptures" } },
  { selector: 'meta[property="og:locale"]', attrs: { property: "og:locale", content: "en_US" } },
  { selector: 'meta[name="twitter:card"]', attrs: { name: "twitter:card", content: "summary_large_image" } },
  { selector: 'meta[name="twitter:title"]', attrs: { name: "twitter:title", content: TITLE } },
  { selector: 'meta[name="twitter:description"]', attrs: { name: "twitter:description", content: DESCRIPTION } },
  { selector: 'meta[name="twitter:image"]', attrs: { name: "twitter:image", content: HERO_IMG } },
  { selector: 'meta[name="robots"]', attrs: { name: "robots", content: "index, follow, max-image-preview:large" } },
  { selector: 'meta[name="googlebot"]', attrs: { name: "googlebot", content: "index, follow" } },
  { selector: 'meta[name="geo.region"]', attrs: { name: "geo.region", content: "US-GA" } },
  { selector: 'meta[name="geo.placename"]', attrs: { name: "geo.placename", content: "Villa Rica, Georgia" } },
  { selector: 'meta[name="geo.position"]', attrs: { name: "geo.position", content: "33.7301;-84.9219" } },
  { selector: 'meta[name="ICBM"]', attrs: { name: "ICBM", content: "33.7301, -84.9219" } },
  { selector: 'meta[name="author"]', attrs: { name: "author", content: "Garden Fountains Yard Sculptures" } },
];

function upsertMeta({ selector, attrs }) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  el.setAttribute("data-seo-injected", "true");
}

function upsertCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function upsertTitle(text) {
  document.title = text;
}

function applySeoTags() {
  upsertTitle(TITLE);
  META.forEach(upsertMeta);
  upsertCanonical(SITE_URL + "/");
  upsertJsonLd("seo-local-business", LOCAL_BUSINESS);
}

export default function SeoHead() {
  useEffect(applySeoTags, []);
  return (
    <>
      <JsonLd id="seo-faq" data={FAQ_SCHEMA} />
      <JsonLd id="seo-breadcrumbs" data={BREADCRUMB_SCHEMA} />
      <JsonLd id="seo-videos" data={VIDEO_SCHEMA} />
    </>
  );
}
